import { useState, useEffect, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { FieldLabelRow } from './field-label-row';
import { LockToggle } from './-components/lock-toggle';
import { hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';

export function SlugField({
	generatedSlug,
	label,
	readOnly,
	optional,
	info,
}: {
	/** The auto-generated slug derived from the source text (e.g. title). */
	generatedSlug: string;
	label: string;
	readOnly?: boolean;
	optional?: boolean;
	info?: ReactNode;
}) {
	const field = useFieldContext<string | undefined>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = readOnly ?? formReadOnly;
	const isOptional = useIsFieldOptional(field.name, optional);

	const value = field.state.value ?? '';

	// Sync with the generated slug only while the field started empty (create flow).
	// Once a slug is persisted, the lock becomes pure edit protection — changes to the
	// source field no longer overwrite the saved slug.
	const [syncEnabled] = useState(() => !value);
	const [locked, setLocked] = useState(true);

	useEffect(() => {
		if (syncEnabled && locked && generatedSlug !== value) {
			field.handleChange(generatedSlug);
		}
		// `field` is a stable handle; we intentionally react to locked/generatedSlug/value.
	}, [locked, generatedSlug, value, syncEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (syncEnabled && !locked && value === generatedSlug && generatedSlug !== '') {
			setLocked(true);
		}
	}, [value, generatedSlug, locked, syncEnabled]);

	const toggleLock = () => {
		const newLocked = !locked;
		setLocked(newLocked);
		if (syncEnabled && newLocked) {
			field.handleChange(generatedSlug);
		}
	};

	return (
		<div>
			<div className="grid gap-2">
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={hasFieldError(field.state.meta)}
						optional={isOptional}
						info={info}
					/>
				)}
				<div
					className={cn(
						'flex h-8 items-center rounded-lg border bg-transparent text-base transition-[color,border-color,padding,box-shadow] md:text-sm',
						isReadOnly
							? 'border-transparent px-0'
							: locked
								? 'border-input/30 bg-muted/50 px-2.5'
								: 'border-input focus-within:border-ring px-2.5',
					)}
				>
					<input
						id={field.name}
						name={field.name}
						aria-invalid={hasFieldError(field.state.meta)}
						value={isReadOnly && !value ? '-' : value}
						onBlur={field.handleBlur}
						onChange={(e) => {
							const v = e.target.value
								.toLowerCase()
								.replace(/[^a-z0-9-]/g, '-')
								.replace(/--+/g, '-');
							field.handleChange(v);
						}}
						readOnly={isReadOnly || locked}
						tabIndex={isReadOnly ? -1 : undefined}
						className={cn(
							'min-w-0 flex-1 bg-transparent outline-none',
							locked && 'text-muted-foreground cursor-default',
						)}
						placeholder="url-slug"
					/>
					{!isReadOnly && <LockToggle locked={locked} onToggle={toggleLock} />}
				</div>
			</div>
		</div>
	);
}

export default SlugField;
